import {LinkExternalIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {getGroupCreatedAt} from '../utils/get-group-created-at'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {CrossReferencedEvent$key} from './__generated__/CrossReferencedEvent.graphql'
import styles from './CrossReferencedEvent.module.css'
import {IssueLink} from './IssueLink'
import {Ago} from './row/Ago'
import {TimelineRow} from './row/TimelineRow'

export type ReferenceTypes = 'issues' | 'prs' | 'mixed'

type RollupGroup = CrossReferencedEvent$key & {source?: {__typename: string}; createdAt?: string}
type RollupGroups = Record<string, RollupGroup[]>

type CrossReferencedEventProps = {
  queryRef: CrossReferencedEvent$key & {createdAt?: string}
  rollupGroup?: RollupGroups
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  repositoryId: string
  ownerLogin: string
}

type sourceType = 'Issue' | 'PullRequest'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const CrossReferencedEventFragment = graphql`
  fragment CrossReferencedEvent on CrossReferencedEvent {
    referencedAt
    willCloseTarget
    databaseId
    innerSource: source {
      __typename
      ...IssueLink
    }
    actor {
      ...TimelineRowEventActor
    }
  }
`

export function CrossReferencedEvent({
  queryRef,
  issueUrl,
  highlightedEventId,
  onLinkClick,
  refAttribute,
  rollupGroup,
  repositoryId,
  ownerLogin,
}: CrossReferencedEventProps): React.ReactElement {
  const {actor, referencedAt, willCloseTarget, innerSource, databaseId} = useFragment(
    CrossReferencedEventFragment,
    queryRef,
  )

  const isIssue = innerSource.__typename === 'Issue'
  const isPullRequest = innerSource.__typename === 'PullRequest'

  if (!isIssue && !isPullRequest) {
    return <></>
  }

  const rolledUpGroup = rollupGroup && rollupGroup['CrossReferencedEvent'] ? rollupGroup['CrossReferencedEvent'] : []
  const hasMixedReferenceTypes = rolledUpGroup.some(item => item.source?.__typename !== innerSource.__typename)

  const message = buildMessage(innerSource.__typename, willCloseTarget, rolledUpGroup?.length, hasMixedReferenceTypes)

  const highlighted = String(databaseId) === highlightedEventId

  const itemsToRender = rolledUpGroup.length === 0 ? [queryRef] : rolledUpGroup
  const eventCreatedAt = getGroupCreatedAt(queryRef.createdAt, rolledUpGroup)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={referencedAt}
      showAgoTimestamp={false}
      deepLinkUrl={issueUrl}
      onLinkClick={onLinkClick}
      leadingIcon={LinkExternalIcon}
    >
      <TimelineRow.Main>
        {message}{' '}
        {eventCreatedAt ? (
          <Ago timestamp={new Date(eventCreatedAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        ) : null}
      </TimelineRow.Main>
      <TimelineRow.Secondary>
        <section aria-label={LABELS.crossReferencedEvent.sectionLabel}>
          <ul className={styles.crossReferencesList}>
            {itemsToRender.map((item, index) => (
              <CrossReferenceItem
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${databaseId}_${index}`}
                event={item}
                targetRepositoryId={repositoryId}
                targetOwnerLogin={ownerLogin}
              />
            ))}
          </ul>
        </section>
      </TimelineRow.Secondary>
    </TimelineRow>
  )
}

function CrossReferenceItem({
  event,
  targetRepositoryId,
  targetOwnerLogin,
}: {
  event: CrossReferencedEvent$key
  targetRepositoryId: string
  targetOwnerLogin: string
}) {
  const {innerSource} = useFragment(CrossReferencedEventFragment, event)
  return (
    <li style={{listStyle: 'none'}}>
      <IssueLink data={innerSource} targetRepositoryId={targetRepositoryId} targetOwnerLogin={targetOwnerLogin} />
    </li>
  )
}

function buildMessage(
  sourceType: sourceType,
  willCloseTarget: boolean,
  rolledUpGroupLength: number,
  mixedReferenceTypes: boolean,
) {
  if (willCloseTarget) {
    return LABELS.timeline.linkedAClosingPR
  }
  if (rolledUpGroupLength === 0 || mixedReferenceTypes) {
    return LABELS.timeline.mentionedThisIn
  }
  if (sourceType === 'Issue') {
    return `${LABELS.timeline.mentionedThisIn} in ${rolledUpGroupLength} issues`
  } else {
    return `${LABELS.timeline.mentionedThisIn} in ${rolledUpGroupLength} pull requests`
  }
}
